import pLimit from 'p-limit';

import {withExponentialBackoff} from 'util/requestWithExponentialBackoff';

import type {
  ActionableGoogleMimeBehavior,
  DirectDownloadOutputFileInfo,
  DownloadResult,
  DownloadSingleGoogleDriveFileInstructions,
  ExportOutputFileInfo,
  GoogleDriveIssue,
  GoogleMimeTypeBehavior,
  InputFileInfo,
} from './ValidateAndDownloadGoogleDriveFilesTypes';
import {
  createInvalidFilePickedDownloadResult,
  createUnableToDirectDownloadFileIssue,
  createUnableToExportFileIssue,
  GoogleDriveUnreachableIssue,
} from './ValidateAndDownloadGoogleDriveFilesTypes';

const GOOGLE_MIME_TYPE_PREFIX = 'application/vnd.google';

// The maximum number of download/export requests we want to wait for in parallel. We are capping this to reduce the number of concurrent requests to Google (to avoid things like rate limiting)
export const CONCURRENCY_LIMIT = 3;

const GOOGLE_MIME_TYPES: Record<string, GoogleMimeTypeBehavior> = {
  // Not allowed

  // Third party shortcut
  'application/vnd.google-apps.drive-sdk': {action: 'not_allowed'},
  // Google Form
  'application/vnd.google-apps.form': {action: 'not_allowed'},
  // Google Fusion Tables
  'application/vnd.google-apps.fusiontable': {action: 'not_allowed'},
  // Gmail layout template
  'application/vnd.google-apps.mail-layout': {action: 'not_allowed'},
  // Google My Maps
  'application/vnd.google-apps.map': {action: 'not_allowed'},
  // Google Apps Script
  'application/vnd.google-apps.script': {action: 'not_allowed'},
  // Drive shortcut
  'application/vnd.google-apps.shortcut': {action: 'not_allowed'},
  // Google Sites
  'application/vnd.google-apps.site': {action: 'not_allowed'},
  // Google Vids -- a video project in the Google Vids video editor
  'application/vnd.google-apps.vid': {action: 'not_allowed'},

  // Direct download -- some of these may not be downloadable; if the download fails, we'll return an error to the user

  // Google internal pointer tag for audio -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.audio': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google tag for generic file -- may be able to download this, may not
  'application/vnd.google-apps.file': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google internal pointer tag for photo -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.photo': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Google internal pointer tag for video -- most likely can't download this, but we will attempt to
  'application/vnd.google-apps.video': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },
  // Gemini Gem -- a template for Gemini conversations
  'application/vnd.google-gemini.gem': {action: 'direct', outMime: 'application/json', extension: '.json'},
  // Unknown type -- may be able to download this, may not
  'application/vnd.google-apps.unknown': {
    action: 'direct',
    outMime: 'application/octet-stream',
    extension: '.bin',
  },

  // Export

  // Google Docs exports to PDF
  'application/vnd.google-apps.document': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
  // Google Drawing exports to PDF
  'application/vnd.google-apps.drawing': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
  // Google Slides exports to .pptx
  'application/vnd.google-apps.presentation': {
    action: 'export',
    outMime: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
    extension: '.pptx',
  },
  // Google Sheets exports to .xlsx
  'application/vnd.google-apps.spreadsheet': {
    action: 'export',
    outMime: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    extension: '.xlsx',
  },
  // Google Jamboard exports to PDF -- most likely users won't even have a jamboard file because it's deprecated
  'application/vnd.google-apps.jam': {action: 'export', outMime: 'application/pdf', extension: '.pdf'},
};

function getActionableGoogleMimeBehavior(mimeType: string): ActionableGoogleMimeBehavior | undefined {
  if (!Object.hasOwn(GOOGLE_MIME_TYPES, mimeType)) {
    return undefined;
  }
  const behavior = GOOGLE_MIME_TYPES[mimeType];
  if (behavior.action === 'not_allowed') {
    return undefined;
  }
  return behavior;
}

export class GooglePickerAPIScriptNotLoadedError extends Error {
  constructor() {
    super('Google Picker API script must be loaded before calling downloadGoogleDriveFiles');
    this.name = 'GooglePickerAPIScriptNotLoadedError';
  }
}

/**
 * Generates output filename for direct download of Google mime-type files.
 * Keeps the base of inputFileName the same, but if an extension already exists, overwrite it with outputFileExtension (otherwise just add outputFileExtension)
 *
 * Note: we are assuming that whatever stuff follows the last period in the filename is the original file extension, and
 * we overwrite that with the new file extension. This may not always be true--for example, a file could be named "doc.v2", in
 * which case we strip the v2 and replace it with the new file extension. This is a tradeoff we purposefully made to avoid having
 * to import the mime-types library to check if there is a valid file extension in the original name.
 */
export function generateOutputFilenameForDirectDownloadOfGoogleMimeFiles(
  inputFileName: string,
  outputFileExtension: string
): string {
  return inputFileName.includes('.')
    ? inputFileName.replace(/\.[^.]+$/, outputFileExtension)
    : inputFileName + outputFileExtension;
}

export async function downloadGoogleDriveFile(
  accessToken: string,
  instructions: DownloadSingleGoogleDriveFileInstructions
): Promise<DownloadResult> {
  const googleApiUrl =
    instructions.action === 'direct'
      ? `https://www.googleapis.com/drive/v3/files/${instructions.inputFileInfo.inputFileId}?alt=media`
      : `https://www.googleapis.com/drive/v3/files/${instructions.inputFileInfo.inputFileId}/export?mimeType=${instructions.outputFileInfo.outputFileMimeType}`;

  const {response, error} = await withExponentialBackoff<GoogleDriveIssue>(
    () =>
      fetch(googleApiUrl, {
        headers: {Authorization: `Bearer ${accessToken}`},
      }),
    {
      errorWhenRetriesExhausted: GoogleDriveUnreachableIssue,
    }
  );

  if (error != null) {
    return {fileId: instructions.inputFileInfo.inputFileId, file: null, error};
  }

  // If there was a non-retryable http error code exporting the file, return an error
  if (!response.ok) {
    return {
      fileId: instructions.inputFileInfo.inputFileId,
      file: null,
      error:
        instructions.action === 'direct'
          ? await createUnableToDirectDownloadFileIssue(instructions.inputFileInfo, response)
          : await createUnableToExportFileIssue(
              instructions.inputFileInfo,
              instructions.outputFileInfo,
              response
            ),
    };
  }

  const blob = await response.blob();

  return {
    fileId: instructions.inputFileInfo.inputFileId,
    file: new File([blob], instructions.outputFileInfo.outputFileName, {
      type: instructions.outputFileInfo.outputFileMimeType,
    }),
    error: null,
  };
}

/**
 * Generator function that downloads Google drive files (either by a direct download or an export to a different filetype).
 * At each step, returns a DownloadResult, which contains either a File or an error object of type GoogleDriveIssue.
 *
 * @param documentInfos - list of information on the documents we want to either direct download or export
 * @param accessToken - the Google oAuth access token for this user
 * @param concurrencyLimit - the maximum number of download/export requests we want to wait for in parallel.
 *   We are capping this to ensure we don't have too many concurrent requests hitting Google at once (to prevent things like rate limiting).
 */
export async function* validateAndDownloadGoogleDriveFiles(
  documentInfos: Array<google.picker.DocumentObject>,
  accessToken: string,
  concurrencyLimit: number = CONCURRENCY_LIMIT
): AsyncGenerator<DownloadResult> {
  if (typeof google === 'undefined' || !google.picker?.Document) {
    throw new GooglePickerAPIScriptNotLoadedError();
  }

  // Validate that concurrency limit is a positive number. If its not, fall back to default CONCURRENCY_LIMIT
  if (concurrencyLimit <= 0) {
    concurrencyLimit = CONCURRENCY_LIMIT;
  }

  const limiter = pLimit(concurrencyLimit);

  // Map of all of the promises to download/export files that we will kick off concurrently (limited by CONCURRENCY_LIMIT)
  const pendingFileRetrievalTasks = new Map<number, Promise<{taskId: number; value: DownloadResult}>>();

  let taskId: number = 0;

  // Loop through all documentInfos. Yield a DownloadResult with an error description if an invalid file is picked. Otherwise, construct
  // the promise to download or export the file, and add it to pendingFileRetrievalTasks.

  for (const documentInfo of documentInfos) {
    const fileName = documentInfo[google.picker.Document.NAME];
    const fileId = documentInfo[google.picker.Document.ID];
    const mimeType = documentInfo[google.picker.Document.MIME_TYPE];

    if (fileId == null) {
      yield createInvalidFilePickedDownloadResult(
        '',
        fileName
          ? `File "${fileName}" is invalid and can't be uploaded. Try a different file`
          : "A selected file is invalid and can't be uploaded.",
        `Unable to get id for file with name "${fileName ?? ''}"`
      );
      continue;
    }
    if (fileName == null) {
      yield createInvalidFilePickedDownloadResult(
        fileId,
        `File with id "${fileId}" is invalid and can't be uploaded. Try a different file`,
        `Unable to get name for file with id "${fileId}"`
      );
      continue;
    }
    if (mimeType == null) {
      yield createInvalidFilePickedDownloadResult(
        fileId,
        `File "${fileName}" is invalid and can't be uploaded. Try a different file`,
        `Unable to get mimeType for file "${fileName}"`
      );
      continue;
    }

    const inputFileInfo: InputFileInfo = {
      inputFileId: fileId,
      inputFileMimeType: mimeType,
      inputFileName: fileName,
    };

    let fileRetrievalTask;

    if (mimeType.startsWith(GOOGLE_MIME_TYPE_PREFIX)) {
      const behavior = getActionableGoogleMimeBehavior(mimeType);
      if (behavior === undefined) {
        const [userDescription, devDescription] = Object.hasOwn(GOOGLE_MIME_TYPES, mimeType)
          ? [
              `File "${fileName}" has a type that is not allowed for upload`,
              `File "${fileName}" with mimeType "${mimeType}" not allowed for upload`,
            ]
          : [
              `File "${fileName}" has an unrecognized type for upload`,
              `File "${fileName}" with mimeType "${mimeType}" not recognized for upload`,
            ];
        yield createInvalidFilePickedDownloadResult(fileId, userDescription, devDescription);
        continue;
      }

      if (behavior.action === 'direct') {
        const outputFileInfo: DirectDownloadOutputFileInfo = {
          outputFileName: generateOutputFilenameForDirectDownloadOfGoogleMimeFiles(
            fileName,
            behavior.extension
          ),
          outputFileMimeType: behavior.outMime,
        };

        fileRetrievalTask = () =>
          downloadGoogleDriveFile(accessToken, {
            action: 'direct',
            inputFileInfo,
            outputFileInfo,
          });
      } else {
        // Action is export
        // Output file name should just the current filename with the output file extension appended
        const outputFileInfo: ExportOutputFileInfo = {
          outputFileName: fileName + behavior.extension,
          outputFileMimeType: behavior.outMime,
          outputFileExtension: behavior.extension,
        };

        fileRetrievalTask = () =>
          downloadGoogleDriveFile(accessToken, {
            action: 'export',
            inputFileInfo,
            outputFileInfo,
          });
      }
    } else {
      // For all non-Google mime types, attempt to direct download them, keeping filename and mimetype the same
      const outputFileInfo: DirectDownloadOutputFileInfo = {
        outputFileName: fileName,
        outputFileMimeType: mimeType,
      };

      fileRetrievalTask = () =>
        downloadGoogleDriveFile(accessToken, {
          action: 'direct',
          inputFileInfo,
          outputFileInfo,
        });
    }

    const currentTaskId = taskId;
    pendingFileRetrievalTasks.set(
      currentTaskId,
      limiter(fileRetrievalTask).then((value) => ({taskId: currentTaskId, value}))
    );

    taskId += 1;
  }

  // Fire off all file retrieval tasks, yielding them in the order they finish.
  while (pendingFileRetrievalTasks.size > 0) {
    const {taskId, value} = await Promise.race(pendingFileRetrievalTasks.values());
    pendingFileRetrievalTasks.delete(taskId);
    yield value;
  }
}
